import { ITEM_TYPES } from '@brickloop/core/schemas';
import { useEffect, useState, type FormEvent } from 'react';
import { useCategories, useColors } from '../queries';
import { Button, Checkbox, Field, Input, Select } from '../ui';

/** The filter fields, as they appear in the URL. */
export const FILTER_KEYS = [
  'attention',
  'q',
  'itemType',
  'condition',
  'colorId',
  'categoryId',
  'location',
  'status',
  'priceLocked',
  'qtyMin',
  'qtyMax',
  'priceMin',
  'priceMax',
  'sellThroughMin',
  'sellThroughMax',
  'monthsMin',
  'monthsMax',
  'marketData',
  'includeEmpty',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type Filters = Partial<Record<FilterKey, string>>;

export const pickFilters = (params: URLSearchParams): Filters =>
  Object.fromEntries(FILTER_KEYS.flatMap((k) => (params.get(k) ? [[k, params.get(k)!]] : [])));

/**
 * The two searches people ask for most. They only fill in the boxes, so the numbers stay visible and can be
 * changed. Sell-through is the share of what is listed or sold on BrickLink that sold: 50% or more is brisk,
 * 20% or less is slow.
 */
const PRESETS = {
  sellsWell: { label: 'Sells well, I have little', hint: 'Sell-through of 50% or more, and 10 or fewer in stock. Change the numbers afterwards if you like.', set: { sellThroughMin: '50', qtyMax: '10' } },
  slowMover: { label: 'Slow mover, I have a lot', hint: 'Sell-through of 20% or less, and 50 or more in stock. Change the numbers afterwards if you like.', set: { sellThroughMax: '20', qtyMin: '50' } },
} as const;
const PRESET_FIELDS: FilterKey[] = ['sellThroughMin', 'sellThroughMax', 'monthsMin', 'monthsMax', 'marketData', 'qtyMin', 'qtyMax'];

const TYPE_LABEL: Record<string, string> = {
  PART: 'Part',
  SET: 'Set',
  MINIFIG: 'Minifigure',
  BOOK: 'Book',
  GEAR: 'Gear',
  CATALOG: 'Catalogue',
  INSTRUCTION: 'Instructions',
  ORIGINAL_BOX: 'Original box',
  UNSORTED_LOT: 'Unsorted lot',
};

export function LotFilters({ value, onApply }: { value: Filters; onApply: (f: Filters) => void }) {
  const [draft, setDraft] = useState<Filters>(value);
  const colors = useColors();
  const categories = useCategories();
  useEffect(() => setDraft(value), [value]);

  const set = (k: FilterKey) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const text = (k: FilterKey) => ({ value: draft[k] ?? '', onChange: (e: { target: { value: string } }) => set(k)(e.target.value) });

  function applyPreset(key: keyof typeof PRESETS) {
    const kept = Object.entries(draft).filter(([k, v]) => v && !PRESET_FIELDS.includes(k as FilterKey));
    onApply(Object.fromEntries([...kept, ...Object.entries(PRESETS[key].set)]) as Filters);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    onApply(Object.fromEntries(Object.entries(draft).filter(([, v]) => v)) as Filters);
  }

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Filter inventory">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <Field label="Search" className="col-span-2">
          <Input type="search" placeholder="Part number, name or EAN" {...text('q')} />
        </Field>
        <Field label="Type">
          <Select {...text('itemType')}>
            <option value="">Any</option>
            {ITEM_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Condition">
          <Select {...text('condition')}>
            <option value="">Any</option>
            <option value="N">New</option>
            <option value="U">Used</option>
          </Select>
        </Field>
        <Field label="Colour">
          <Select {...text('colorId')}>
            <option value="">Any</option>
            {colors.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select {...text('categoryId')}>
            <option value="">Any</option>
            {categories.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Location" hint="A* matches A1, A2…">
          <Input placeholder="e.g. A*" {...text('location')} />
        </Field>
        <Field label="Status">
          <Select {...text('status')}>
            <option value="">Any</option>
            <option value="draft">Draft</option>
            <option value="live">Live</option>
            <option value="stockroom">Stockroom</option>
            <option value="sold_out">Sold out</option>
          </Select>
        </Field>
        <Field label="Price lock">
          <Select {...text('priceLocked')}>
            <option value="">Any</option>
            <option value="true">Locked</option>
            <option value="false">Not locked</option>
          </Select>
        </Field>
        <Field label="Quantity">
          <div className="flex items-center gap-1">
            <Input inputMode="numeric" placeholder="min" aria-label="Minimum quantity" {...text('qtyMin')} />
            <Input inputMode="numeric" placeholder="max" aria-label="Maximum quantity" {...text('qtyMax')} />
          </div>
        </Field>
        <Field label="Price">
          <div className="flex items-center gap-1">
            <Input inputMode="decimal" placeholder="min" aria-label="Minimum price" {...text('priceMin')} />
            <Input inputMode="decimal" placeholder="max" aria-label="Maximum price" {...text('priceMax')} />
          </div>
        </Field>
      </div>
      <fieldset className="rounded-md border border-slate-200 p-3">
        <legend className="px-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">BrickLink market (all sellers)</legend>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          <Field label="Sell-through %" hint="50+ sells well, 20 or less is slow">
            <div className="flex items-center gap-1">
              <Input inputMode="decimal" placeholder="min" aria-label="Minimum sell-through percent" {...text('sellThroughMin')} />
              <Input inputMode="decimal" placeholder="max" aria-label="Maximum sell-through percent" {...text('sellThroughMax')} />
            </div>
          </Field>
          <Field label="Months of stock" hint="No recent sales counts as endless">
            <div className="flex items-center gap-1">
              <Input inputMode="decimal" placeholder="min" aria-label="Minimum months of stock" {...text('monthsMin')} />
              <Input inputMode="decimal" placeholder="max" aria-label="Maximum months of stock" {...text('monthsMax')} />
            </div>
          </Field>
          <Field label="Market figures">
            <Select {...text('marketData')}>
              <option value="">Any</option>
              <option value="have">Have figures</option>
              <option value="none">No figures yet</option>
            </Select>
          </Field>
          <div className="col-span-2 flex flex-wrap items-end gap-2 md:col-span-4 xl:col-span-3">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((k) => (
              <Button key={k} variant="secondary" small title={PRESETS[k].hint} onClick={() => applyPreset(k)}>
                {PRESETS[k].label}
              </Button>
            ))}
          </div>
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit">Search</Button>
        <Button variant="secondary" onClick={() => onApply({})}>
          Clear
        </Button>
        <Checkbox
          label="Include lots with no stock"
          checked={draft.includeEmpty === 'true'}
          onChange={(v) => set('includeEmpty')(v ? 'true' : '')}
        />
      </div>
    </form>
  );
}
